import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Navigation, Clock, Car, CheckCircle, Timer, Route, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stepLabels = {
  pending: "En attente de confirmation",
  en_route: "Chauffeur en route",
  arrived: "Chauffeur arrive",
  started: "Course en cours",
  completed: "Course terminee",
};

const formatTime = (isoStr) => {
  if (!isoStr) return "-";
  try { return new Date(isoStr).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return "-"; }
};

export const RideSummaryPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API}/quick-booking/${bookingId}/summary`);
        setSummary(res.data);
      } catch { setSummary(null); }
      setLoading(false);
    };
    fetchSummary();
    const interval = setInterval(fetchSummary, 10000);
    return () => clearInterval(interval);
  }, [bookingId]);

  if (loading) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
    </div>
  );

  if (!summary) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="text-center">
        <p className="text-zinc-400 mb-4">Reservation introuvable</p>
        <Button onClick={() => navigate("/")} className="btn-primary"><ArrowLeft className="w-4 h-4 mr-2" /> Accueil</Button>
      </div>
    </div>
  );

  const { ride } = summary;
  const isActive = ["en_route", "arrived", "started"].includes(ride.step);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="px-4 py-4 border-b border-zinc-800">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-zinc-800" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <Car className="w-5 h-5 text-emerald-400" />
          <h1 className="text-lg font-bold text-white font-['Outfit']">Ma course</h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        {/* Status banner */}
        <div className={`rounded-xl p-4 border ${
          ride.step === "completed" ? "bg-emerald-500/10 border-emerald-500/30" :
          isActive ? "bg-amber-500/10 border-amber-500/30" :
          "bg-zinc-900 border-zinc-800"
        }`}>
          <div className="flex items-center gap-3">
            {ride.step === "completed" ? <CheckCircle className="w-6 h-6 text-emerald-400" /> :
             isActive ? <Car className="w-6 h-6 text-amber-400 animate-pulse" /> :
             <Clock className="w-6 h-6 text-zinc-400" />}
            <div>
              <p className="text-white font-bold" data-testid="ride-status">{stepLabels[ride.step] || ride.step}</p>
              <p className="text-zinc-400 text-sm">{summary.pickup_date} a {summary.pickup_time}</p>
            </div>
          </div>
        </div>

        {/* Map preview */}
        {summary.maps_preview_url && (
          <div className="rounded-xl overflow-hidden border border-zinc-800">
            <img src={summary.maps_preview_url} alt="Apercu trajet" className="w-full h-48 object-cover" data-testid="maps-preview" />
          </div>
        )}

        {/* Route details */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-3 h-3 rounded-full bg-emerald-400 mt-1 flex-shrink-0" />
            <div>
              <p className="text-zinc-500 text-xs">Depart</p>
              <p className="text-white text-sm" data-testid="pickup-address">{summary.pickup_address}</p>
            </div>
          </div>
          {(summary.stops || []).map((stop, idx) => (
            <div key={idx} className="flex items-start gap-3 ml-1">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400 mt-1 flex-shrink-0 border border-zinc-800" />
              <div>
                <p className="text-zinc-500 text-xs">Arret {idx + 1}</p>
                <p className="text-white text-sm">{stop.address}</p>
                {ride.stop_waits?.[idx]?.wait_minutes > 0 && (
                  <p className="text-amber-400 text-xs flex items-center gap-1"><Timer className="w-3 h-3" /> Attente: {ride.stop_waits[idx].wait_minutes} min</p>
                )}
              </div>
            </div>
          ))}
          <div className="flex items-start gap-3">
            <div className="w-3 h-3 rounded-full bg-red-400 mt-1 flex-shrink-0" />
            <div>
              <p className="text-zinc-500 text-xs">Destination</p>
              <p className="text-white text-sm" data-testid="dest-address">{summary.destination_address}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          {summary.distance_km && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
              <Route className="w-5 h-5 text-blue-400 mx-auto mb-1" />
              <p className="text-white font-bold text-lg" data-testid="distance">{summary.distance_km} km</p>
              <p className="text-zinc-500 text-xs">Distance</p>
            </div>
          )}
          {(ride.ride_duration_minutes > 0 || summary.duration_min) && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
              <Clock className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
              <p className="text-white font-bold text-lg" data-testid="duration">{ride.ride_duration_minutes > 0 ? Math.round(ride.ride_duration_minutes) : Math.round(summary.duration_min)} min</p>
              <p className="text-zinc-500 text-xs">Duree</p>
            </div>
          )}
          {ride.pickup_wait_minutes > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
              <Timer className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <p className="text-white font-bold text-lg">{ride.pickup_wait_minutes} min</p>
              <p className="text-zinc-500 text-xs">Attente arrivee</p>
            </div>
          )}
          {ride.total_wait_minutes > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
              <Timer className="w-5 h-5 text-orange-400 mx-auto mb-1" />
              <p className="text-white font-bold text-lg" data-testid="total-wait">{ride.total_wait_minutes} min</p>
              <p className="text-zinc-500 text-xs">Attente totale</p>
            </div>
          )}
        </div>

        {/* Price */}
        <div className="bg-zinc-900 border border-emerald-500/30 rounded-xl p-4 text-center">
          <p className="text-zinc-400 text-sm mb-1">Prix total</p>
          <p className="text-emerald-400 font-bold text-3xl" data-testid="total-price">{summary.total_price}€</p>
          <p className="text-zinc-500 text-xs mt-1">{summary.payment_method === "cash" ? "Especes" : summary.payment_method === "stripe" ? "Carte bancaire" : summary.payment_method}</p>
        </div>

        {/* Timeline */}
        {ride.step !== "pending" && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <h3 className="text-white font-semibold text-sm mb-3">Chronologie</h3>
            <div className="space-y-2">
              {ride.en_route_at && <div className="flex items-center gap-2 text-sm"><Navigation className="w-4 h-4 text-blue-400" /><span className="text-zinc-400">En route :</span><span className="text-white">{formatTime(ride.en_route_at)}</span></div>}
              {ride.arrived_at && <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-amber-400" /><span className="text-zinc-400">Arrive :</span><span className="text-white">{formatTime(ride.arrived_at)}</span></div>}
              {ride.started_at && <div className="flex items-center gap-2 text-sm"><Car className="w-4 h-4 text-emerald-400" /><span className="text-zinc-400">Depart course :</span><span className="text-white">{formatTime(ride.started_at)}</span></div>}
              {ride.completed_at && <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-zinc-400">Arrivee :</span><span className="text-white">{formatTime(ride.completed_at)}</span></div>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
